import { useState, useEffect } from 'react';
import { AuthProvider } from '@sdk/authentication/AuthContext';
import { SessionProvider } from '@sdk/session/SessionContext';
import { SaveClientFlags } from '@sdk/authentication/types';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { BillingInfoType } from '@sdk/account/api/AccountApi';
import useSlingApi from '@sdk/api/useSlingApi';
import { extendBillingInfo } from '@sdk/account/utils';

import { NavigationProvider } from '@web/navigation/NavigationProvider';
import { NavigateAction } from '@web/navigation/navigationProvider.types';
import { SocketConnectionProvider } from '@web/components/SocketConnectionProvider/SocketConnectionProvider';
import ExpiryWarningModals from '@web/account/billingWarnings/ExpiryWarningModals/ExpiryWarningModals';
import { MissingBillingAddressModal } from '@web/account/billingWarnings/MissingBillingAddressModal/MissingBillingAddressModal';
import { BillingInfoProvider } from '@web/account/billing/BillingInfoContext';

type Props = {
  currentUser: AuthUserType;
  saveClientFlags: SaveClientFlags;
  socketConnection: any;
  stateService: {
    go: (path: string, params?: object) => void;
  };
};

export const BillingWarningsAngular = ({
  stateService,
  currentUser,
  socketConnection,
  saveClientFlags,
}: Props) => {
  const api = useSlingApi();
  const [billingInfo, setBillingInfo] = useState<BillingInfoType>();

  useEffect(() => {
    const getBillingInfo = async () => {
      const { data } = await api.account.getBillingInfo();
      setBillingInfo(extendBillingInfo(data));
    };

    if (currentUser?.isManager) {
      getBillingInfo();
    }
  }, [api.account, currentUser]);

  if (!currentUser) {
    return null;
  }

  const navigate: NavigateAction = (path, params = undefined) => {
    stateService.go(path, params);
  };

  return (
    <AuthProvider currentUser={currentUser}>
      <SessionProvider>
        <NavigationProvider navigate={navigate}>
          <SocketConnectionProvider connection={socketConnection}>
            <BillingInfoProvider billingInfo={billingInfo} onBillingInfoUpdated={setBillingInfo}>
              <ExpiryWarningModals saveClientFlags={saveClientFlags} />
              <MissingBillingAddressModal
                saveClientFlags={saveClientFlags}
                currentUser={currentUser}
              />
            </BillingInfoProvider>
          </SocketConnectionProvider>
        </NavigationProvider>
      </SessionProvider>
    </AuthProvider>
  );
};
